import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { VALUE_CHAIN_MAP_CUSTOM_NODES, VALUE_CHAIN_MAP_MODALS } from 'constants/interfaces';
import {
  useCreateBoardColumnMutation,
  useDeleteBoardColumnMutation,
  useUpdateBoardColumnMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type { IValueChainBoardColumn } from 'store/slices/valueChainMap/interfaces/IValueChainBoardColumn';
import type IMessageDistrictError from '../interfaces/IMessageDistrictError';
import type { IUseValueChainSwimlanesParams } from '../interfaces/IUseValueChainSwimlanes';

const useValueChainSwimlanes = ({
  nodes,
  valueChainMapId,
  selectedBoard,
  setSelectedBoard,
  setActiveModal,
  closeModal,
  creatingTargetIdRef,
}: IUseValueChainSwimlanesParams) => {
  const { t } = useTranslation();
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [districtErrors, setDistrictErrors] = useState<IMessageDistrictError | undefined>(undefined);

  const [updateBoardColumn, { isLoading: isUpdateBoardColumnLoading }] = useUpdateBoardColumnMutation();
  const [createBoardColumn, { isLoading: isCreateBoardColumnLoading }] = useCreateBoardColumnMutation();
  const [deleteBoardColumn, { isLoading: isDeleteBoardColumnLoading }] = useDeleteBoardColumnMutation();

  const checkIsSwimlane = (name: string) => nodes.find((node) => node.data.name === name
    && node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE);

  const onUpdateSwimlaneOrderIndex = (orderIndex: number, swimlane?: IValueChainBoardColumn) => {
    const boardToUpdate = swimlane || selectedBoard;

    if (!boardToUpdate) return;

    updateBoardColumn({
      id: Number(boardToUpdate.id),
      orderIndex,
      name: boardToUpdate.name || '',
    })
      .unwrap()
      .then(() => {
        toast.success(t('notifications.swimlaneOrderIndexHasBeenUpdated'));
        closeModal();
      });
  };

  const onAddSwimlane = (name: string) => {
    if (checkIsSwimlane(name)) {
      toast.error(t('notifications.swimlaneAlreadyExists'));
      return;
    }
    createBoardColumn({ valueChainMapId: Number(valueChainMapId), name, boardId: Number(creatingTargetIdRef.current) })
      .unwrap()
      .then(() => {
        toast.success(t('notifications.swimlaneHasBeenAdded'));
        closeModal();
      });
  };

  const onEditSwimlane = useCallback((board: IValueChainBoardColumn) => {
    setActiveModal(VALUE_CHAIN_MAP_MODALS.RENAME);
    setSelectedBoard(board);
  }, [setActiveModal, setSelectedBoard]);

  const onDeleteSwimlane = useCallback((board: IValueChainBoardColumn) => {
    setDeleteModalOpen(true);
    setActiveModal(undefined);
    setSelectedBoard(board);
  }, [setActiveModal, setSelectedBoard]);

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedBoard(undefined);
  };

  const onDeleteSwimlaneConfirm = () => {
    closeDeleteModal();
    deleteBoardColumn({ id: Number(selectedBoard?.id) })
      .unwrap()
      .then(() => {
        toast.success(t('notifications.swimlaneHasBeenDeleted'));
        closeModal();
      }).catch((error) => {
        setDistrictErrors(error.data.message);
        setActiveModal(VALUE_CHAIN_MAP_MODALS.REFUSED_DELETE_SWIMLANE);
      });
    closeDeleteModal();
  };

  const onRenameSubmit = (name: string) => {
    if (checkIsSwimlane(name)) {
      toast.error(t('notifications.swimlaneAlreadyExists'));
      return;
    }
    updateBoardColumn({
      id: Number(selectedBoard?.id),
      orderIndex: Number(selectedBoard?.orderIndex),
      name,
    })
      .unwrap()
      .then(() => {
        toast.success(t('notifications.swimlaneHasBeenRenamed'));
        closeModal();
      });
  };

  return {
    isDeleteModalOpen,
    setDeleteModalOpen,
    districtErrors,
    onUpdateSwimlaneOrderIndex,
    onAddSwimlane,
    onEditSwimlane,
    onDeleteSwimlane,
    onDeleteSwimlaneConfirm,
    onRenameSubmit,
    isUpdateBoardColumnLoading,
    isCreateBoardColumnLoading,
    isDeleteBoardColumnLoading,
  };
};

export default useValueChainSwimlanes;
